import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { FormProvider } from "react-hook-form";
import { FormInput } from "@/components/form/rhf/FormInput";
import { FormField } from "@/components/form/FormField";
import KnowledgePicker from "@/components/shared/pickers/knowledge-picker";
import MediaAssetPicker from "@/components/shared/pickers/media-asset-picker";
import { useFeatureEnabled } from "@/features/features";
import { useUpdateStep } from "@/hooks/api/mutations/workflows.mutations";
import type { WorkflowStep } from "@/types/workflow.types";
import { AgenticModelSelect } from "./agentic-model-select";
import { HighlightedCodeEditor } from "./highlighted-code-editor";

const schema = z.object({
  modelId: z.string().min(1, "Model is required"),
  promptTemplate: z.string().min(1, "Prompt is required"),
  responseFormat: z.enum(["text", "json"]),
  maxTokens: z.number().int().positive().optional(),
  temperature: z.number().min(0).max(2).optional(),
  knowledgeIds: z.array(z.string()),
  mediaAssetIds: z.array(z.string()),
});

type FormValues = z.infer<typeof schema>;

function toFormValues(config: Record<string, unknown>): FormValues {
  return {
    modelId: String(config.model_id ?? ""),
    promptTemplate: String(config.prompt_template ?? ""),
    responseFormat: config.response_format === "json" ? "json" : "text",
    maxTokens: typeof config.max_tokens === "number" ? config.max_tokens : undefined,
    temperature: typeof config.temperature === "number" ? config.temperature : undefined,
    knowledgeIds: Array.isArray(config.knowledge_ids) ? (config.knowledge_ids as string[]) : [],
    mediaAssetIds: Array.isArray(config.media_asset_ids)
      ? (config.media_asset_ids as string[])
      : [],
  };
}

function toPayload(values: FormValues): Record<string, unknown> {
  return {
    model_id: values.modelId,
    prompt_template: values.promptTemplate,
    response_format: values.responseFormat,
    max_tokens: values.maxTokens ?? null,
    temperature: values.temperature ?? null,
    knowledge_ids: values.knowledgeIds,
    media_asset_ids: values.mediaAssetIds,
  };
}

interface LlmCallConfigFormProps {
  step: WorkflowStep;
  workflowId: string;
  onSuccess?: () => void;
}

export function LlmCallConfigForm({ step, workflowId, onSuccess }: LlmCallConfigFormProps) {
  const t = useTranslations("workflows");
  const updateStep = useUpdateStep();
  const kbEnabled = useFeatureEnabled("knowledge_base");

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(step.config),
  });

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const knowledgeIds = watch("knowledgeIds");
  const mediaAssetIds = watch("mediaAssetIds");
  const responseFormat = watch("responseFormat");

  const onSubmit = handleSubmit(async (values) => {
    await updateStep.mutateAsync({
      workflowId,
      stepId: step.id,
      config: toPayload(values),
    });
    onSuccess?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <AgenticModelSelect
          name="modelId"
          label={t("drawer.field.model_id")}
          required
        />

        <FormField label={t("drawer.field.prompt")} required error={errors.promptTemplate?.message}>
          <Controller
            control={control}
            name="promptTemplate"
            render={({ field }) => (
              <HighlightedCodeEditor
                language="markdown"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                rows={6}
                minHeight={150}
                placeholder="Enter your prompt template…"
                invalid={!!errors.promptTemplate}
              />
            )}
          />
        </FormField>

        <FormField label={t("drawer.field.response_format")} error={errors.responseFormat?.message}>
          <select
            {...methods.register("responseFormat")}
            className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
          >
            <option value="text">Text</option>
            <option value="json">JSON</option>
          </select>
          {responseFormat === "json" && (
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              JSON mode — model output must be valid JSON.
            </p>
          )}
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormInput
            name="maxTokens"
            label={t("drawer.field.max_tokens")}
            type="number"
            placeholder="4096"
          />
          <FormInput
            name="temperature"
            label={t("drawer.field.temperature")}
            type="decimal"
            placeholder="0.7"
          />
        </div>

        <FormField label={t("drawer.field.knowledge_ids")}>
          <KnowledgePicker
            value={knowledgeIds}
            onChange={(ids) => setValue("knowledgeIds", ids, { shouldDirty: true })}
            disabled={!kbEnabled}
            placeholder={kbEnabled ? undefined : t("drawer.feature_disabled.knowledge_base")}
            nodeType={step.stepType}
            fieldKey="knowledge_ids"
          />
        </FormField>

        <FormField label={t("drawer.field.media_asset_ids")}>
          <MediaAssetPicker
            value={mediaAssetIds}
            onChange={(ids) => setValue("mediaAssetIds", ids, { shouldDirty: true })}
            kinds={["image", "video", "audio", "raw_doc"]}
            nodeType={step.stepType}
            fieldKey="media_asset_ids"
          />
        </FormField>

        {updateStep.isError && (
          <p className="text-xs text-[var(--destructive)]">{t("drawer.save_error")}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || updateStep.isPending}
          className="h-9 w-full rounded-lg bg-[var(--primary)] text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90 disabled:opacity-60"
        >
          {isSubmitting || updateStep.isPending ? t("drawer.saving") : t("drawer.save")}
        </button>
      </form>
    </FormProvider>
  );
}

export default LlmCallConfigForm;
